import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

import { LoadingSpinner } from '~components/Order/ordersDispatchStyledComponents';
import { CompanySettings } from '~hooks/useCompanySettings/models';
import useCompanySettings from '~hooks/useCompanySettings/useCompanySettings';
import SettingsPageHeader from '~pages/Settings/components/SettingsPageHeader';
import {
  companySettingCategoryLabelKeys,
  quickBooksPageCategories,
  settingKeysInCategory,
} from '~pages/Settings/Configuration/companySettingsCatalog';
import CompanySettingToggle from '~pages/Settings/Configuration/components/CompanySettingToggle';
import useVisibleCompanySettingKeys from '~pages/Settings/Configuration/useVisibleCompanySettingKeys';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import DefaultUntaxedTaxRateScheduleSetting from './DefaultUntaxedTaxRateScheduleSetting';
import LineDescriptionFormatSection from './LineDescriptionFormatSection';
import SharedConnectionSetting from './SharedConnectionSetting';

const I18N_PREFIX =
  'administration.integration.rutter.quick_books_online.advanced_configuration';

// The QuickBooks page shows the company picked in the company selector, so its settings
// must be read and written for that company too - the store only holds the user's own.
const QuickBooksAdvancedConfiguration = observer(() => {
  const { userStore } = useStores();
  const { getManagedCompanySettings } = useCompanySettings();
  const visibleSettingKeys = useVisibleCompanySettingKeys();
  const effectiveCompanyId = getEffectiveUserCompanyId(userStore);
  const isUserCompany = effectiveCompanyId === userStore.userCompany.id;
  const [managedCompanySettings, setManagedCompanySettings] =
    useState<CompanySettings | null>(null);
  const [didManagedFetchFail, setDidManagedFetchFail] = useState(false);

  useEffect(() => {
    if (isUserCompany) {
      return;
    }

    let isCurrentCompany = true;
    setManagedCompanySettings(null);
    setDidManagedFetchFail(false);

    const fetchManagedCompanySettings = async () => {
      try {
        const settings = await getManagedCompanySettings(effectiveCompanyId);
        if (isCurrentCompany && settings) {
          setManagedCompanySettings(settings);
        }
      } catch {
        // getManagedCompanySettings already toasted via handleRequestError
        if (isCurrentCompany) {
          setDidManagedFetchFail(true);
        }
      }
    };

    void fetchManagedCompanySettings();

    return () => {
      isCurrentCompany = false;
    };
  }, [effectiveCompanyId, getManagedCompanySettings, isUserCompany]);

  const companySettings = isUserCompany
    ? userStore.companySettings
    : managedCompanySettings;

  return (
    <Box sx={{ maxWidth: '1100px' }}>
      <SettingsPageHeader
        description={t(`${I18N_PREFIX}.description`)}
        title={t(`${I18N_PREFIX}.title`)}
      />
      {didManagedFetchFail && (
        <Alert severity="error" sx={{ my: 2 }} variant="outlined">
          {t('error_messages.company_settings.failed_to_fetch')}
        </Alert>
      )}
      {!didManagedFetchFail && !companySettings && (
        <Box sx={{ py: 2 }}>
          <LoadingSpinner isVisible={true} loadingIndicatorSize={16} />
        </Box>
      )}
      <SharedConnectionSetting />
      {companySettings &&
        quickBooksPageCategories.map((category) => {
          const categorySettingKeys = settingKeysInCategory(visibleSettingKeys, category);

          if (categorySettingKeys.length === 0) {
            return null;
          }

          return (
            <Box key={category} sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'normal' }}>
                {t(companySettingCategoryLabelKeys[category])}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {categorySettingKeys.map((key) => (
                  <CompanySettingToggle
                    companyId={effectiveCompanyId}
                    companySettings={companySettings}
                    key={`${effectiveCompanyId}-${key}`}
                    settingKey={key}
                  />
                ))}
              </Box>
              <Divider sx={{ mt: 2 }} />
            </Box>
          );
        })}
      <LineDescriptionFormatSection
        companyId={effectiveCompanyId}
        key={effectiveCompanyId}
      />
      {companySettings && (
        <DefaultUntaxedTaxRateScheduleSetting
          companyId={effectiveCompanyId}
          companySettings={companySettings}
          key={`default-untaxed-${effectiveCompanyId}`}
        />
      )}
    </Box>
  );
});

export default QuickBooksAdvancedConfiguration;
